import { say } from '@app/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTRPC } from '../lib/trpc'
import { Notice } from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import { Give, NamedHeading, Sections, Visit } from './home-sections'
import { Masthead } from './Masthead'

/**
 * The front door of an org whose site leads with giving: its name, the ways to
 * give, and how to reach it.
 *
 * The same bands a shul's front door closes on, with nothing above them — an
 * org with no luach has no next occasion to lead with, and inventing a hero in
 * its place would be a claim with nothing behind it.
 */
export function GivingHomePage({
  orgId,
  onLogin,
}: {
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const orgs = useQuery(trpc.org.list.queryOptions())
  const org = orgs.data?.find((o) => o.shortId === orgId)
  const masthead = useQuery(
    trpc.site.publicMasthead.queryOptions({ shortId: orgId }),
  )
  const [mastheadEl, setMastheadEl] = useState<HTMLElement | null>(null)

  // The page waits for its masthead too, so nothing jumps down under it.
  const loading = orgs.isLoading || masthead.isLoading
  if (loading || !org) {
    return (
      <PublicShell name={null} orgId={null} onLogin={onLogin}>
        {loading ? <Notice>Loading…</Notice> : <PublicNotFound />}
      </PublicShell>
    )
  }

  return (
    <PublicShell
      name={org.name}
      nameHe={org.nameHe}
      language={org.publicLanguage}
      orgId={org.shortId}
      acceptsDonations={org.acceptsDonations}
      contact={org}
      bleed
      masthead={masthead.data ? mastheadEl : null}
      onLogin={onLogin}
    >
      <div className="home">
        {masthead.data ? (
          <Masthead
            ref={setMastheadEl}
            html={masthead.data.html}
            title={org.name}
          />
        ) : (
          <NamedHeading
            name={say(org.publicLanguage.name, org.name, org.nameHe)}
          />
        )}
        <Sections orgId={org.shortId} afterGiving={false} />
        {org.acceptsDonations ? <Give orgId={org.shortId} /> : null}
        <Sections orgId={org.shortId} afterGiving />
        <Visit org={org} />
      </div>
    </PublicShell>
  )
}
